import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { CheckPermissionsGuard, Condition } from '@nimbus-global/features/shared/services/routing/check-permissions.guard';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { NimbusPermissionSet } from '@nimbus-global/features/permissions/models/enums/nimbus-permission-set.enum';
import { TeamSelectGuard } from '@nimbus-admin/features/organization-unit/services/team-select.guard';
import { PartnerSelectGuard } from '@nimbus-admin/features/organization-unit/services/partner-select.guard';
import { TenantSelectGuard } from '@nimbus-admin/features/organization-unit/services/tenant-select.guard';
import { licensingAdminPermissionSets } from '@nimbus-global/features/licenses/utils/licensing-admin-metadata.extractor';
import { CheckFeatureFlippingInFeatureManagementGuard } from '@nimbus-global/features/shared/services/routing/check-feature-flipping-in-feature-management.guard';
import { createGlobalPermissionsModelGuard } from '@nimbus-global/features/shared/services/routing/global-permissions-model.guard';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { companionSplitScreenExpandGuard } from '@nimbus-library/features/companion-chat/companion-split-screen-expand.guard';
import { NimbusAdminRoutes } from './nimbus-admin.routes';

const checkCompanionConversationGuard = createGlobalPermissionsModelGuard((model) => model?.tenant?.allowCompanionConversation ?? false);

const routes: Routes = [
  {
    path: NimbusAdminRoutes.Overview,
    loadChildren: () => import('../features/overview/overview.module').then((m) => m.OverviewModule),
    canActivate: [TenantSelectGuard, CheckPermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permissions: [NimbusPermission.ReadTenantsOverview],
      fallbackUrl: ['/', NimbusAdminRoutes.Teams.root],
      trackingModule: 'Overview',
    },
  },
  {
    path: NimbusAdminRoutes.Partners,
    loadChildren: () => import('../features/partner/partner.module').then((m) => m.PartnerModule),
    canActivate: [CheckPermissionsGuard],
    data: { permissionSet: NimbusPermissionSet.NimbusAdmin, fallbackUrl: ['/', NimbusAdminRoutes.Overview], trackingModule: 'Partners' },
  },
  {
    path: NimbusAdminRoutes.Tenants,
    loadChildren: () => import('../features/tenant/tenant.module').then((m) => m.TenantModule),
    canActivate: [PartnerSelectGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permissions: [NimbusPermission.ManageDedicatedTenants, NimbusPermission.ManagePartnerTenantsList],
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
      trackingModule: 'Tenants',
    },
  },
  {
    path: NimbusAdminRoutes.Teams.root,
    loadChildren: () => import('../features/team/team.module').then((m) => m.TeamModule),
    canActivate: [TenantSelectGuard, CheckPermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permission: NimbusPermission.ServicesRead,
      permissionSets: [NimbusPermissionSet.OuAdmin, NimbusPermissionSet.WorkflowAdminLimited],
      condition: Condition.OR,
      tryLocationChange: true,
      fallbackUrl: ['/', NimbusAdminRoutes.Users],
      trackingModule: 'Services',
    },
  },
  {
    path: NimbusAdminRoutes.Users,
    loadChildren: () => import('../features/user/user.module').then((m) => m.UserModule),
    canActivate: [TenantSelectGuard, CheckPermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permission: NimbusPermission.UsersRead,
      permissionSet: NimbusPermissionSet.OuAdmin,
      condition: Condition.OR,
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
      trackingModule: 'Users',
    },
  },
  {
    path: NimbusAdminRoutes.Tenant,
    loadChildren: () => import('../features/tenant-settings/tenant-settings.module').then((m) => m.TenantSettingsModule),
    canActivate: [TenantSelectGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: { permission: NimbusPermission.ManageTenant, fallbackUrl: ['/', NimbusAdminRoutes.Overview], trackingModule: 'Tenant' },
  },
  {
    path: NimbusAdminRoutes.TeamReporting,
    loadChildren: () => import('../features/team-reporting/team-reporting.module').then((m) => m.TeamReportingModule),
    canActivate: [TeamSelectGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permission: NimbusPermission.ManageTenant,
      permissionSet: NimbusPermissionSet.OuAdmin,
      condition: Condition.OR,
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
    },
  },
  {
    path: NimbusAdminRoutes.Operations.root,
    loadChildren: () => import('../features/operations/operations.module').then((m) => m.OperationsModule),
    canActivate: [CheckPermissionsGuard],
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    data: {
      permissions: [
        NimbusPermission.ViewAdminOperationsChangeHistoryMarker,
        NimbusPermission.ViewAdminOperationsCustomersMarker,
        NimbusPermission.ViewAdminOperationsFeatureFlagVisibilityMarker,
        NimbusPermission.ViewAdminOperationsServicesMarker,
        NimbusPermission.ViewAdminOperationsTaskManagementMarker,
      ],
      permissionSet: NimbusPermissionSet.NimbusAdmin,
      condition: Condition.OR,
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
    },
  },
  {
    path: NimbusAdminRoutes.ExternalTools,
    loadChildren: () => import('../features/external-tools/external-tools.module').then((m) => m.ExternalToolsModule),
    canActivate: [CheckPermissionsGuard],
    data: { permissionSet: NimbusPermissionSet.NimbusAdmin, fallbackUrl: ['/', NimbusAdminRoutes.Overview] },
  },
  // Keeps bookmarks of the former Operations Tools page working.
  { path: 'operations-tools/:linkId', redirectTo: `/${NimbusAdminRoutes.ExternalTools}/:linkId` },
  { path: 'operations-tools', redirectTo: `/${NimbusAdminRoutes.ExternalTools}`, pathMatch: 'full' },
  {
    path: NimbusAdminRoutes.OperationsManage,
    loadChildren: () => import('../features/operations-manage/operations-manage.module').then((m) => m.OperationsManageModule),
    canActivate: [CheckPermissionsGuard],
    data: { permissionSet: NimbusPermissionSet.NimbusAdmin, fallbackUrl: ['/', NimbusAdminRoutes.Overview] },
  },
  {
    path: NimbusAdminRoutes.TeamManage.root,
    loadChildren: () => import('../features/manage/manage.module').then((m) => m.ManageModule),
    canActivate: [TenantSelectGuard],
    data: {
      permission: NimbusPermission.ManageTenant,
      permissionSets: [NimbusPermissionSet.OuAdmin, NimbusPermissionSet.WorkflowAdminLimited],
      condition: Condition.OR,
      baseUrl: NimbusAdminRoutes.TeamManage.root,
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
      trackingModule: 'Configuration',
    },
    runGuardsAndResolvers: 'always',
  },
  {
    path: NimbusAdminRoutes.Tools,
    loadChildren: () => import('../features/tools/tools.module').then((m) => m.ToolsModule),
    canActivate: [CheckPermissionsGuard],
    data: { permissionSet: NimbusPermissionSet.NimbusAdmin, fallbackUrl: ['/', NimbusAdminRoutes.Overview] },
  },
  {
    path: NimbusAdminRoutes.Authentication,
    loadChildren: () =>
      import('../features/authentication-process/authentication-process.module').then((m) => m.AuthenticationProcessModule),
  },
  {
    path: NimbusAdminRoutes.Licensing,
    loadChildren: () => import('../features/licensing/licensing.module').then((m) => m.LicensingModule),
    canActivate: [CheckPermissionsGuard],
    data: {
      permissionSets: [NimbusPermissionSet.NimbusAdmin, ...licensingAdminPermissionSets],
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
      trackingModule: 'Licensing',
    },
  },
  {
    path: NimbusAdminRoutes.CompanionConversation,
    loadChildren: () => import('../../../../shared/src/features/companion-chat/companion-chat.routes').then((m) => m.companionChatRoutes),
    canActivate: [
      companionSplitScreenExpandGuard,
      TenantSelectGuard,
      CheckFeatureFlippingInFeatureManagementGuard,
      checkCompanionConversationGuard,
    ],
    data: {
      fallbackUrl: ['/', NimbusAdminRoutes.Overview],
      features: [Feature.CompanionConversation],
      trackingModule: 'CompanionChat',
    },
  },
  {
    path: '**',
    redirectTo: '/overview',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class RoutingModule {}
